import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "components/ui/Button";
import { UseOriQaSessionReturn } from "apps/user/learner/hooks/use-ori-qa-session";
import {
  fetchQaConfigurations,
  createQaThread,
  archiveQaThread,
  QaConfigurationSummary,
} from "apps/user/learner/api/coach-qa";
import OriDrawer from "./OriDrawer";
import OriQaThreads from "./OriQaThreads";
import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./OriQaDrawer.module.scss";

interface OriQaDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  session: UseOriQaSessionReturn;
  // Close the drawer once a thread is opened (new or resumed) so the chat
  // behind it is visible. The chat reconnects to the thread on its own once
  // the session's connection params change.
  onThreadOpened: () => void;
}

// Ori's own default agenda — preferred as the picker's initial choice so the
// common case stays one click, and matches what the endpoint opens in when no
// conversation_type is sent.
const DEFAULT_AGENDA = "general_conversation";

// QA testing drawer. Uses the shared OriDrawer shell (same styling/animation as
// the chat-history drawer) and supplies the configuration picker, a "+ New chat"
// action, the past-threads list (resume), and an "Archive this chat" action for
// the open thread. A bounded thread's memory isolation is enforced in the
// backend rather than chosen here, so the panel states it and offers no control
// for it. Opened from the header once QA mode is on; the main chat stays the
// full view behind it.
const OriQaDrawer: React.FC<OriQaDrawerProps> = ({
  isOpen,
  onClose,
  session,
  onThreadOpened,
}) => {
  const { t } = useTranslation(["ori"]);
  const {
    selectedConfigId,
    setSelectedConfigId,
    activeThread,
    openThread,
    closeThread,
  } = session;

  const [configurations, setConfigurations] = useState<
    QaConfigurationSummary[]
  >([]);
  // Which agenda the *next* new thread opens in. Local, not session state: it's
  // read once at creation and then baked into the conversation, and a resumed
  // thread carries its own agenda.
  const [selectedAgenda, setSelectedAgenda] = useState<string | null>(null);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState(false);
  // Bumped after a new thread or an archive so the list reflects the change.
  const [threadsRefreshKey, setThreadsRefreshKey] = useState(0);

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!isOpen || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    let cancelled = false;
    setIsLoadingConfigs(true);
    setLoadError(false);
    (async () => {
      try {
        const result = await fetchQaConfigurations();
        if (cancelled) return;
        setConfigurations(result);
      } catch (error) {
        if (cancelled) return;
        logError("Ori QA configuration list fetch failed", error);
        setLoadError(true);
      } finally {
        if (!cancelled) setIsLoadingConfigs(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Only the agendas the selected Configuration actually serves — offering any
  // other would just 400 on create.
  const agendas = useMemo(
    () =>
      configurations.find((config) => config.id === selectedConfigId)?.agendas ??
      [],
    [configurations, selectedConfigId]
  );

  // Keep the agenda choice valid for whatever Configuration is selected,
  // preferring general_conversation so the common case stays one click.
  useEffect(() => {
    setSelectedAgenda((current) => {
      if (agendas.some((agenda) => agenda.conversation_type === current)) {
        return current;
      }
      const preferred = agendas.find(
        (agenda) => agenda.conversation_type === DEFAULT_AGENDA
      );
      return (
        preferred?.conversation_type ?? agendas[0]?.conversation_type ?? null
      );
    });
  }, [agendas]);

  const handleNewThread = useCallback(async () => {
    if (!selectedConfigId || !selectedAgenda) return;
    setIsCreating(true);
    setCreateError(false);
    try {
      const thread = await createQaThread(selectedConfigId, selectedAgenda);
      openThread({
        threadId: thread.thread_id,
        conversationId: thread.conversation_id,
      });
      setThreadsRefreshKey((key) => key + 1);
      onThreadOpened();
    } catch (error) {
      logError("Ori QA new-thread failed", error);
      setCreateError(true);
    } finally {
      setIsCreating(false);
    }
  }, [selectedConfigId, selectedAgenda, openThread, onThreadOpened]);

  const handleArchive = useCallback(async () => {
    if (!activeThread) return;
    setIsArchiving(true);
    setArchiveError(false);
    try {
      await archiveQaThread(activeThread.threadId);
      closeThread();
      setThreadsRefreshKey((key) => key + 1);
    } catch (error) {
      logError("Ori QA archive-thread failed", error);
      setArchiveError(true);
    } finally {
      setIsArchiving(false);
    }
  }, [activeThread, closeThread]);

  const threadOpen = activeThread !== null;

  return (
    <OriDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="QA testing"
      testId="ori-qa-drawer"
    >
      {loadError ? (
        <p className={styles.error} data-testid="ori-qa-load-error">
          {t("ori:qa.loadError")}
        </p>
      ) : (
        <label className={styles.field}>
          <span className={styles.lbl}>{t("ori:qa.configuration")}</span>
          <select
            className={styles.select}
            data-testid="ori-qa-config-select"
            value={selectedConfigId ?? ""}
            disabled={isLoadingConfigs}
            onChange={(event) => setSelectedConfigId(event.target.value || null)}
          >
            <option value="">
              {isLoadingConfigs ? "Loading…" : "Select a configuration"}
            </option>
            {configurations.map((config) => (
              <option
                key={config.id}
                value={config.id}
                disabled={!config.composes}
                title={config.compose_error ?? undefined}
              >
                {config.composes
                  ? `${config.name} (${config.status})`
                  : t("ori:qa.configBroken", {
                      name: config.name,
                      status: config.status,
                    })}
              </option>
            ))}
          </select>
        </label>
      )}

      {selectedConfigId && (
        <label className={styles.field}>
          <span className={styles.lbl}>{t("ori:qa.agenda")}</span>
          {agendas.length === 0 ? (
            <span className={styles.hint} data-testid="ori-qa-agenda-none">
              {t("ori:qa.agendaNone")}
            </span>
          ) : (
            <>
              <select
                className={styles.select}
                data-testid="ori-qa-agenda-select"
                value={selectedAgenda ?? ""}
                onChange={(event) => setSelectedAgenda(event.target.value)}
              >
                {agendas.map((agenda) => (
                  <option
                    key={agenda.conversation_type}
                    value={agenda.conversation_type}
                  >
                    {agenda.name}
                  </option>
                ))}
              </select>
              <span className={styles.hint}>{t("ori:qa.agendaHint")}</span>
            </>
          )}
        </label>
      )}

      <div className={styles.field}>
        <span className={styles.lbl}>{t("ori:qa.memory")}</span>
        <span className={styles.hint} data-testid="ori-qa-routing-memory-note">
          {t("ori:qa.memoryNote")}
        </span>
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          size="small"
          disabled={!selectedConfigId || !selectedAgenda || isCreating}
          onClick={handleNewThread}
          data-testid="ori-qa-new-thread"
        >
          {t("ori:qa.newThread")}
        </Button>
        {threadOpen && (
          <Button
            variant="ghost"
            size="small"
            disabled={isArchiving}
            onClick={handleArchive}
            data-testid="ori-qa-archive-thread"
          >
            {t("ori:qa.archiveThread")}
          </Button>
        )}
      </div>
      {createError && (
        <p className={styles.error} data-testid="ori-qa-new-thread-error">
          {t("ori:qa.newThreadError")}
        </p>
      )}
      {archiveError && (
        <p className={styles.error} data-testid="ori-qa-archive-error">
          {t("ori:qa.archiveError")}
        </p>
      )}

      {selectedConfigId && (
        <div className={styles.threadsSection}>
          <OriQaThreads
            configId={selectedConfigId}
            agendas={agendas}
            refreshKey={threadsRefreshKey}
            onContinueThread={(thread) => {
              openThread(thread);
              onThreadOpened();
            }}
            activeThreadId={activeThread?.threadId ?? null}
          />
        </div>
      )}
    </OriDrawer>
  );
};

export default OriQaDrawer;
